/**
 * Every standing change, with the day, the time and the reason.
 *
 * The standing panel says where you are. The journal is the only thing that can
 * say *how you got there*, and without it the end-of-shift summary would be a
 * list of numbers that moved for reasons the player has to reconstruct from
 * memory. "Kitchen −13" is data; "Kitchen −13, because you set the Ashford
 * room" is the game explaining its own politics.
 *
 * Capped, because it runs for the length of a save and nobody needs yesterday's
 * reasoning at the resolution of individual ripples. The cap is generous enough
 * that a whole shift always survives.
 */
const LIMIT = 400;

export function createJournal({ standing }) {
  let entries = [];

  standing.onChange((event) => {
    // Unattributed changes are internal bookkeeping — a load, a reset — and
    // putting them in the summary would be noise with no cause to name.
    if (!event.reason) return;
    for (const change of event.changes) {
      if (Math.abs(change.delta) < 0.05) continue;
      entries.push({
        department: change.department,
        delta: change.delta,
        to: change.to,
        reason: event.reason,
        // Stamped by the caller via `at()`, so the journal needs no clock.
        day: current.day,
        minutes: current.minutes,
        primary: change === event.changes[0],
      });
    }
    if (entries.length > LIMIT) entries = entries.slice(-LIMIT);
  });

  let current = { day: 1, minutes: 0 };

  return {
    /** Tells the journal what time it is. Called once a frame. */
    at(clock) {
      current = { day: clock.day, minutes: clock.minutes };
    },

    get entries() {
      return entries;
    },

    /** Everything from one day, newest last. */
    forDay(day) {
      return entries.filter((entry) => entry.day === day);
    },

    /**
     * One day's movement per department, with the causes that drove it.
     *
     * Causes are merged by reason and sorted by how much they moved, because a
     * summary that lists eleven separate two-point ripples is a summary nobody
     * reads.
     */
    summaryForDay(day) {
      const out = new Map();
      for (const entry of this.forDay(day)) {
        let row = out.get(entry.department);
        if (!row) out.set(entry.department, (row = { department: entry.department, delta: 0, to: entry.to, causes: new Map() }));
        row.delta += entry.delta;
        row.to = entry.to;
        row.causes.set(entry.reason, (row.causes.get(entry.reason) ?? 0) + entry.delta);
      }
      return [...out.values()]
        .map((row) => ({
          ...row,
          causes: [...row.causes.entries()]
            .map(([reason, delta]) => ({ reason, delta }))
            .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)),
        }))
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    },

    clear() {
      entries = [];
    },

    toJSON: () => ({ entries }),
    load(state) {
      entries = [...(state?.entries ?? [])];
    },
  };
}
